import React, { useState } from "react";
import CandleTapTask from "./CandleTapTask";
import FailureReveal from "./FailureReveal";
import ReadComplete from "../../core/ReadComplete";
import { CANDLES, READ, REWARDS } from "./config";
import { explainMistakes } from "./explain";
import { validateOhlc } from "../../candle/candleModel";

// Every unguided round, in order: Call It, then one fresh candle per retry, as
// many retries as this Read allows (READ.maxRetries).
const ROUNDS = [CANDLES.callIt, ...CANDLES.retries.slice(0, READ.maxRetries)];

// The Read: Teach (guided) -> Call It (unguided, feedback held until Submit)
// -> reveal -> Retry on a fresh candle after a miss, round after round, until
// the learner gets every element right or the fresh candles run out. The
// learner is never trapped: the Read always completes, and only the
// first-attempt bonus depends on getting Call It right first time.
//
// After a miss, round 1 gets the paced reveal and every later round the compact
// one (FailureReveal.jsx). Concept explanations are gated on the concept, not
// the round: each is shown the first time it is missed and never again.
//
// Phase 1 prototype: state lives in this component only. Nothing is saved and
// no coins are credited; reloading the page starts the Read again.
export default function CandleAnatomyRead() {
  const [beat, setBeat] = useState("teach");
  const [round, setRound] = useState(0);
  const [firstAttemptCorrect, setFirstAttemptCorrect] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [failed, setFailed] = useState(null);
  const [explained, setExplained] = useState(() => new Set());

  // Reject invalid OHLC rather than silently drawing something wrong.
  const invalid = [["teach", CANDLES.teach], ...ROUNDS.map((c, i) => [`round ${i + 1}`, c])]
    .map(([name, c]) => [name, validateOhlc(c)])
    .filter(([, err]) => err);
  if (CANDLES.retries.length < READ.maxRetries) invalid.push(["retries", "fewer retry candles than READ.maxRetries"]);
  if (invalid.length) {
    // eslint-disable-next-line no-console
    console.error("Candle Anatomy: invalid OHLC", invalid);
    return <p className="text-sm text-secondary">This Read could not be loaded.</p>;
  }

  if (beat === "teach") {
    return <CandleTapTask key="teach" ohlc={CANDLES.teach} mode="guided" onContinue={() => setBeat("round")} />;
  }

  if (beat === "round") {
    const ohlc = ROUNDS[round];
    const hasFreshCandle = round < ROUNDS.length - 1;

    const handleSubmitted = (r) => {
      if (round === 0) setFirstAttemptCorrect(r.allCorrect);
      if (r.allCorrect) return;
      // Concept explanations not yet shown; they are marked shown now.
      const fresh = explainMistakes(ohlc, r).filter((m) => !explained.has(m.key));
      setExplained(new Set([...explained, ...fresh.map((m) => m.key)]));
      setFailed({ results: r, explanations: fresh });
    };

    if (failed) {
      return (
        <FailureReveal
          key={`reveal-${round}`}
          ohlc={ohlc}
          results={failed.results}
          paced={round === 0}
          explanations={failed.explanations}
          showNoWick={round === 0}
          ctaLabel={hasFreshCandle ? "Retry" : "Continue"}
          onContinue={() => {
            setFailed(null);
            if (hasFreshCandle) setRound(round + 1);
            else setBeat("complete");
          }}
        />
      );
    }

    // A fully correct round keeps the existing correct-answer reveal and goes
    // straight to completion.
    return (
      <CandleTapTask
        key={`round-${round}`}
        ohlc={ohlc}
        mode="unguided"
        showNoWick={round === 0}
        hintEnabled={READ.hint.enabled && round === 0}
        onHintOpened={() => setHintUsed(true)}
        onSubmitted={handleSubmitted}
        onContinue={() => setBeat("complete")}
      />
    );
  }

  return <ReadComplete rewards={REWARDS} bonus={firstAttemptCorrect && !hintUsed} />;
}
